import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import BlogCard from '../components/BlogCard';
import { getProviderRecommendation } from '../services/openaiService';
import { useSEO } from '../utils/seo';
import { allBlogPosts } from '../data/blog';

const ProviderSelector: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [recommendation, setRecommendation] = useState<string>('');
  const [error, setError] = useState('');

  useSEO({
    title: 'AI Provider Selector - Find the Best LLM: OpenAI, Claude, Gemini & More | Alertaio',
    description: 'Free AI-powered tool to compare and choose the perfect AI provider. Get personalized recommendations for OpenAI GPT-4, Anthropic Claude, Google Gemini, or other LLMs based on your requirements.',
    keywords: 'AI provider selector, compare AI models, OpenAI vs Claude, GPT-4 vs Gemini, best LLM provider, AI model comparison, choose AI provider, LLM selection tool',
    canonicalUrl: 'https://alertaio.com/provider-selector',
    ogImage: 'https://alertaio.com/og-provider-selector.png',
    type: 'website'
  });

  useEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: 'AI Provider Selector',
      description: 'Get AI-powered recommendations to choose the best AI provider (OpenAI, Claude, Gemini, etc.) for your specific use case',
      applicationCategory: 'BusinessApplication',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD'
      },
      featureList: 'Multi-provider comparison, Use case analysis, Cost optimization, Personalized recommendations'
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const [formData, setFormData] = useState({
    productDescription: '',
    userType: '',
    features: '',
    traffic: '',
    compliance: '',
    budgetSensitivity: '',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.productDescription.trim()) {
      setError('Please describe your product');
      return;
    }

    setLoading(true);
    setError('');
    setRecommendation('');

    try {
      const data = await getProviderRecommendation(formData);
      setRecommendation(data.recommendation);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || err.message || 'Failed to get recommendation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navigation />
      
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 mb-4">
              AI Provider Selector - Find Your Best Fit: OpenAI, Claude, Gemini & More
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-4">
              Answer a few questions about your use case, and our AI will analyze all major providers 
              (OpenAI GPT-4, Anthropic Claude, Google Gemini, and more) to recommend the best fit for your needs.
            </p>
            <div className="flex items-center justify-center gap-2 flex-wrap">
              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                ✨ Free AI Analysis
              </span>
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                🔍 All Major Providers
              </span>
              <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                🎯 Best Fit Recommendation
              </span>
            </div>
          </div>

          {/* Form Card */}
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-8 mb-8">
            <div className="space-y-6">
              {/* Product Description */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  What does your product do? *
                </label>
                <textarea
                  name="productDescription"
                  value={formData.productDescription}
                  onChange={handleInputChange}
                  placeholder="E.g., A customer support chatbot that helps users troubleshoot technical issues..."
                  rows={4}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                  required
                />
              </div>

              {/* User Type */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Who are the users?
                </label>
                <select
                  name="userType"
                  value={formData.userType}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="">Select user type</option>
                  <option value="B2C">B2C (Business to Consumer)</option>
                  <option value="B2B">B2B (Business to Business)</option>
                  <option value="Internal">Internal Team/Employees</option>
                  <option value="Mixed">Mixed (B2B and B2C)</option>
                </select>
              </div>

              {/* Core Features */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Core AI features needed
                </label>
                <input
                  type="text"
                  name="features"
                  value={formData.features}
                  onChange={handleInputChange}
                  placeholder="E.g., Chat, search, recommendations, vision, code generation..."
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Expected Traffic */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Expected traffic
                  </label>
                  <select
                    name="traffic"
                    value={formData.traffic}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="">Select traffic level</option>
                    <option value="Low">Low (&lt;1K requests/day)</option>
                    <option value="Medium">Medium (1K-100K requests/day)</option>
                    <option value="High">High (&gt;100K requests/day)</option>
                  </select>
                </div>

                {/* Budget Sensitivity */}
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Budget sensitivity
                  </label>
                  <select
                    name="budgetSensitivity"
                    value={formData.budgetSensitivity}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  >
                    <option value="">Select budget priority</option>
                    <option value="Low">Low (Quality over cost)</option>
                    <option value="Medium">Medium (Balanced)</option>
                    <option value="High">High (Cost-conscious)</option>
                  </select>
                </div>
              </div>

              {/* Compliance */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Any compliance or data-privacy concerns?
                </label>
                <input
                  type="text"
                  name="compliance"
                  value={formData.compliance}
                  onChange={handleInputChange}
                  placeholder="E.g., HIPAA, GDPR, SOC 2, or 'None'"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                {error}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || !formData.productDescription.trim()}
              className="w-full mt-8 py-4 bg-gradient-to-r from-indigo-600 to-blue-600 text-white font-semibold rounded-lg hover:from-indigo-700 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Analyzing your requirements...
                </span>
              ) : (
                'Get AI Provider Recommendation'
              )}
            </button>
          </form>

          {/* Recommendation Results */}
          {recommendation && (
            <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-green-600 rounded-lg flex items-center justify-center text-2xl flex-shrink-0">
                  ✅
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">
                    Personalized Recommendation
                  </h2>
                  <p className="text-slate-600">
                    Based on your requirements, here's what we recommend:
                  </p>
                </div>
              </div>

              <div className="prose max-w-none">
                <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-lg p-6 border border-slate-200">
                  <div className="text-slate-700 whitespace-pre-wrap leading-relaxed">
                    {recommendation}
                  </div>
                </div>
              </div>

              {/* CTA */}
              <div className="mt-8 p-6 bg-indigo-50 rounded-lg border-l-4 border-indigo-500">
                <h3 className="font-semibold text-indigo-900 mb-2">
                  🚀 Ready to implement?
                </h3>
                <p className="text-indigo-800 text-sm mb-4">
                  Set up budget alerts for your chosen provider and never overspend on API costs.
                </p>
                <a
                  href="/register"
                  className="inline-block px-6 py-3 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Create Free Account
                </a>
              </div>
            </div>
          )}

          {/* Info Cards */}
          <div className="mt-16 mb-12">
            <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-6 text-center">
                How to Choose the Right AI Provider
              </h2>
              
              <div className="space-y-8">
                <div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <span className="text-2xl">🎯</span>
                    Understanding AI Providers
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    Major AI providers include <strong>OpenAI</strong> (GPT-4, GPT-3.5 Turbo), 
                    <strong>Anthropic</strong> (Claude 3 Opus, Sonnet, Haiku), 
                    <strong>Google</strong> (Gemini Pro, Ultra), <strong>Cohere</strong>, and <strong>Meta</strong> (Llama models).
                    Each provider has unique strengths in pricing, capabilities, speed, context length, and specialization. 
                    The right choice depends on your specific use case, budget, compliance requirements, and quality needs.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <span className="text-2xl">🔍</span>
                    Key Factors to Consider
                  </h3>
                  <ul className="space-y-2 text-slate-600">
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-600 mt-1">•</span>
                      <span><strong>Use Case Fit:</strong> Different providers excel at different tasks (OpenAI for general use, Claude for long documents, Gemini for multimodal)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-600 mt-1">•</span>
                      <span><strong>Pricing:</strong> Ranges from $0.001/1K tokens (GPT-3.5) to $0.06/1K tokens (Claude Opus, GPT-4)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-600 mt-1">•</span>
                      <span><strong>Performance:</strong> Balance speed, quality, and accuracy based on your requirements</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-600 mt-1">•</span>
                      <span><strong>Context Length:</strong> Claude handles 100K+ tokens, GPT-4 Turbo supports 128K tokens, important for long documents</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-600 mt-1">•</span>
                      <span><strong>Special Features:</strong> Function calling, vision, JSON mode, embeddings, fine-tuning availability varies by provider</span>
                    </li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <span className="text-2xl">🤖</span>
                    Our AI-Powered Analysis
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    This tool uses <strong>GPT-4</strong> to analyze your specific requirements and compare all major AI providers 
                    (OpenAI, Anthropic, Google, Cohere, Meta, and more) to recommend the best fit. Our AI considers the nuances of your use case, 
                    expected traffic, budget constraints, compliance needs, and feature requirements to suggest the optimal provider and model. 
                    Try our <Link to="/token-calculator" className="text-indigo-600 hover:text-indigo-700 font-medium underline">Token Calculator</Link> to 
                    estimate costs, or explore our <Link to="/cost-calculator" className="text-indigo-600 hover:text-indigo-700 font-medium underline">Cost Calculator</Link> for 
                    detailed pricing comparison across all providers.
                  </p>
                </div>

                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg p-6 border-l-4 border-indigo-500">
                  <p className="text-slate-700 font-medium mb-2">💡 Pro Tip:</p>
                  <p className="text-slate-600">
                    Once you've chosen your AI provider, set up budget alerts with Alertaio to monitor your API spending 
                    and prevent unexpected costs. Currently supports OpenAI monitoring with more providers coming soon!
                  </p>
                </div>
              </div>
            </div>

            {/* Blog Cards */}
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">
                Learn More About AI Providers & APIs
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                {allBlogPosts
                  .filter(post => 
                    post.tags.some(tag => 
                      ['AI API', 'provider', 'OpenAI', 'Anthropic', 'comparison', 'models', 'integration'].some(keyword => 
                        tag.toLowerCase().includes(keyword.toLowerCase())
                      )
                    ) || post.category === 'AI Development' || post.category === 'AI Basics'
                  )
                  .slice(0, 3)
                  .map((post) => (
                    <BlogCard key={post.slug} post={post} />
                  ))}
              </div>
              <div className="text-center mt-8">
                <Link
                  to="/blog"
                  className="inline-block px-8 py-3 bg-gradient-to-r from-indigo-600 to-blue-600 text-white font-semibold rounded-lg hover:from-indigo-700 hover:to-blue-700 transition-all shadow-lg"
                >
                  View All Articles
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ProviderSelector;
